import React, { useState, useRef, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

interface DateRangePickerProps {
  startDate: Date | null;
  endDate: Date | null;
  onChange: (range: [Date | null, Date | null]) => void;
}

/**
 * AWS Cost Explorer style date range picker
 * - Shows start/end date in the trigger button
 * - Two-month calendar popup
 * - Separate Start date / End date input fields
 * - Auto-select range presets (Past & Future)
 */
export const DateRangePicker: React.FC<DateRangePickerProps> = ({
  startDate,
  endDate,
  onChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [localStart, setLocalStart] = useState<Date | null>(startDate);
  const [localEnd, setLocalEnd] = useState<Date | null>(endDate);
  const containerRef = useRef<HTMLDivElement>(null);

  // Sync local state when props change
  useEffect(() => {
    setLocalStart(startDate);
    setLocalEnd(endDate);
  }, [startDate, endDate]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const formatDate = (date: Date | null): string => {
    if (!date) return '';
    return date.toISOString().split('T')[0];
  };

  const getDurationLabel = (): string => {
    if (!localStart || !localEnd) return '';
    const diffTime = Math.abs(localEnd.getTime() - localStart.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    if (diffDays >= 365) {
      const years = Math.round(diffDays / 365);
      return `Displaying last ${years} year${years > 1 ? 's' : ''}`;
    } else if (diffDays >= 28) {
      const months = Math.round(diffDays / 30);
      return `Displaying last ${months} month${months > 1 ? 's' : ''}`;
    }
    return `Displaying last ${diffDays} day${diffDays > 1 ? 's' : ''}`;
  };

  const handlePreset = (days: number) => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - days);
    setLocalStart(start);
    setLocalEnd(end);
    onChange([start, end]);
  };

  const handleMonthToDate = () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    setLocalStart(start);
    setLocalEnd(now);
    onChange([start, now]);
  };

  const handleCalendarChange = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates;
    setLocalStart(start);
    setLocalEnd(end);
    if (start && end) {
      onChange([start, end]);
    }
  };

  const handleStartInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const date = e.target.value ? new Date(e.target.value + 'T00:00:00') : null;
    if (date && !isNaN(date.getTime())) {
      setLocalStart(date);
      if (localEnd) {
        onChange([date, localEnd]);
      }
    }
  };

  const handleEndInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const date = e.target.value ? new Date(e.target.value + 'T00:00:00') : null;
    if (date && !isNaN(date.getTime())) {
      setLocalEnd(date);
      if (localStart) {
        onChange([localStart, date]);
      }
    }
  };

  return (
    <div ref={containerRef} className="relative">
      {/* Trigger button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex w-full items-center gap-2 rounded-md border border-border bg-card px-3 py-2 text-left text-sm focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring dark:border-border dark:bg-secondary dark:text-white"
        aria-label="Open date range picker"
      >
        <svg className="h-4 w-4 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
        <span className="flex-1">
          {localStart && localEnd
            ? `${formatDate(localStart)} — ${formatDate(localEnd)}`
            : 'Select date range'}
        </span>
        <svg className={`h-4 w-4 text-muted-foreground/70 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Duration label */}
      {localStart && localEnd && (
        <p className="mt-1 text-xs text-muted-foreground">{getDurationLabel()}</p>
      )}

      {/* Popup */}
      {isOpen && (
        <div className="absolute left-0 top-full z-50 mt-2 rounded-lg border border-border bg-card p-4 shadow-xl dark:border-border dark:bg-card" style={{ minWidth: '620px' }}>
          {/* Calendar - two months */}
          <div className="flex justify-center">
            <DatePicker
              selected={localStart}
              startDate={localStart}
              endDate={localEnd}
              onChange={handleCalendarChange}
              selectsRange
              inline
              monthsShown={2}
              maxDate={new Date()}
            />
          </div>

          {/* Start date / End date inputs */}
          <div className="mt-4 grid grid-cols-2 gap-4">
            <div>
              <label className="mb-1 block text-sm font-semibold text-foreground">
                Start date
              </label>
              <input
                type="date"
                value={formatDate(localStart)}
                onChange={handleStartInputChange}
                className="w-full rounded-md border border-border bg-card px-3 py-2 text-sm dark:border-border dark:bg-secondary dark:text-white"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-semibold text-foreground">
                End date
              </label>
              <input
                type="date"
                value={formatDate(localEnd)}
                onChange={handleEndInputChange}
                className="w-full rounded-md border border-border bg-card px-3 py-2 text-sm dark:border-border dark:bg-secondary dark:text-white"
              />
            </div>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">For date, use YYYY-MM-DD.</p>

          {/* Auto-select range presets */}
          <div className="mt-4 border-t border-border pt-4 dark:border-border">
            <h4 className="mb-2 text-sm font-semibold text-foreground">
              Auto-select range (Relative)
            </h4>
            <p className="mb-3 text-xs text-muted-foreground">
              Choose from the range we've selected for you. The duration will remain the same and the date range will update accordingly each time you open it.
            </p>

            {/* Past presets */}
            <div className="flex flex-wrap items-center gap-1">
              <span className="text-sm font-medium text-foreground/80">Past:</span>
              {[
                { label: '1 Day', days: 1 },
                { label: '7 Days', days: 7 },
              ].map(({ label, days }) => (
                <button
                  key={label}
                  type="button"
                  onClick={() => handlePreset(days)}
                  className="px-2 py-0.5 text-sm text-primary hover:text-primary hover:underline dark:text-primary dark:hover:text-primary"
                >
                  {label}
                </button>
              ))}
              <button
                type="button"
                onClick={handleMonthToDate}
                className="px-2 py-0.5 text-sm text-primary hover:text-primary hover:underline dark:text-primary dark:hover:text-primary"
              >
                Month to Date
              </button>
              {[
                { label: '1 Month', days: 30 },
                { label: '3 Months', days: 90 },
                { label: '6 Months', days: 180 },
                { label: '12 Months', days: 365 },
              ].map(({ label, days }) => (
                <button
                  key={label}
                  type="button"
                  onClick={() => handlePreset(days)}
                  className="px-2 py-0.5 text-sm text-primary hover:text-primary hover:underline dark:text-primary dark:hover:text-primary"
                >
                  {label}
                </button>
              ))}
            </div>

            {/* Future presets */}
            <div className="mt-2 flex flex-wrap items-center gap-1">
              <span className="text-sm font-medium text-foreground/80">Future:</span>
              {[
                { label: '+1 Month', days: 30 },
                { label: '+3 Months', days: 90 },
                { label: '+12 Months', days: 365 },
                { label: '+18 Months', days: 545 },
              ].map(({ label, days }) => (
                <button
                  key={label}
                  type="button"
                  onClick={() => {
                    const start = new Date();
                    const end = new Date();
                    end.setDate(end.getDate() + days);
                    setLocalStart(start);
                    setLocalEnd(end);
                    onChange([start, end]);
                  }}
                  className="px-2 py-0.5 text-sm text-primary hover:text-primary hover:underline dark:text-primary dark:hover:text-primary"
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Close button */}
          <div className="mt-4 flex justify-end border-t border-border pt-3 dark:border-border">
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="rounded-md bg-primary px-4 py-1.5 text-sm font-medium text-white hover:bg-primary/90 dark:bg-primary dark:hover:bg-primary"
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
